import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../Toast';
import { trackSaenggibuUxEvent } from '../../utils/eventTracker';
import './RegenerationChainList.css';

const MAX_CHARS = 2000;

/**
 * 다시 만들 때 함께 반영되는 이전 요청 목록.
 * 글을 누르면 그 자리에서 고치고(Enter 저장·Esc 취소), ×로 지우면 토스트에서 되돌릴 수 있다.
 */
const RegenerationChainList = ({
  items,
  onChange,
  disabled = false,
  surface = 'single',
  category = '',
  title = '이전 요청도 함께 반영해요',
}) => {
  const { showToast, removeToast } = useToast();
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const list = Array.isArray(items) ? items : [];

  useEffect(() => {
    if (editingIndex !== null) inputRef.current?.focus();
  }, [editingIndex]);

  useEffect(() => {
    if (editingIndex !== null && editingIndex >= list.length) setEditingIndex(null);
  }, [editingIndex, list.length]);

  if (list.length === 0) return null;

  const track = (action, extra = {}) => trackSaenggibuUxEvent(action, {
    saenggibu_category: category,
    is_batch: surface === 'batch',
    surface,
    chain_count: list.length,
    ...extra,
  });

  const offerUndo = (message, previous, stage) => {
    let toastId = null;
    toastId = showToast(message, 'info', 6000, {
      label: '되돌리기',
      onClick: () => {
        onChange(previous);
        track('saenggibu_regen_chain_undo', { stage });
        if (toastId !== null && toastId !== undefined) removeToast?.(toastId);
      },
    });
  };

  const startEdit = (index) => {
    if (disabled) return;
    setEditingIndex(index);
    setDraft(list[index]);
  };

  const remove = (index, { silent = false } = {}) => {
    const previous = [...list];
    onChange(list.filter((_, i) => i !== index));
    if (silent) return;
    track('saenggibu_regen_chain_delete');
    offerUndo('요청을 지웠어요', previous, 'delete');
  };

  const commitEdit = () => {
    if (editingIndex === null) return;
    const index = editingIndex;
    const value = draft.trim().slice(0, MAX_CHARS);
    setEditingIndex(null);
    if (!value) {
      remove(index);
      return;
    }
    if (value === list[index]) return;
    const next = list.map((item, i) => (i === index ? value : item));
    onChange(next.filter((item, i) => i === index || item !== value));
    track('saenggibu_regen_chain_edit');
  };

  const clearAll = () => {
    const previous = [...list];
    onChange([]);
    track('saenggibu_regen_chain_clear');
    offerUndo('요청을 모두 지웠어요', previous, 'clear');
  };

  return (
    <div className="regen-chain" data-testid="regen-chain">
      <p className="regen-chain-title">{title}</p>
      <ol className="regen-chain-list">
        {list.map((instruction, index) => (
          <li key={`${index}-${instruction.slice(0, 16)}`} className="regen-chain-item">
            <span className="regen-chain-no" aria-hidden="true">{index + 1}</span>
            {editingIndex === index ? (
              <input
                ref={inputRef}
                className="regen-chain-input"
                value={draft}
                maxLength={MAX_CHARS}
                aria-label={`${index + 1}번 요청 고치기`}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commitEdit}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    commitEdit();
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    setEditingIndex(null);
                  }
                }}
              />
            ) : (
              <button
                type="button"
                className="regen-chain-text"
                onClick={() => startEdit(index)}
                disabled={disabled}
                title="눌러서 고치기"
              >
                {instruction}
              </button>
            )}
            {editingIndex !== index && (
              <span className="regen-chain-actions">
                <button
                  type="button"
                  className="regen-chain-icon"
                  onClick={() => startEdit(index)}
                  disabled={disabled}
                  aria-label={`${index + 1}번 요청 고치기`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="regen-chain-icon"
                  onClick={() => remove(index)}
                  disabled={disabled}
                  aria-label={`${index + 1}번 요청 지우기`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
          </li>
        ))}
      </ol>
      {list.length >= 2 && (
        <button type="button" className="regen-chain-clear" onClick={clearAll} disabled={disabled}>
          모두 지우기
        </button>
      )}
    </div>
  );
};

export default RegenerationChainList;
